import { LitElement, html } from 'lit';
import './quote-terms-accordion.js';
import '../icons/info-filled-icon.js';

/**
 * Quote Acceptance Section Component
 * Displays terms & conditions accordion and Accept Quote button
 * Manages acceptance state and enables/disables button based on term acceptance
 */
class QuoteAcceptanceSection extends LitElement {
    static properties = {
        terms: { type: Array },
        allTermsAccepted: { type: Boolean, state: true },
    };

    // Use Light DOM for Tailwind compatibility
    createRenderRoot() {
        return this;
    }

    constructor() {
        super();
        /** @type {Array<{seq: number, term: string}>} */
        this.terms = [];
        this.allTermsAccepted = false;
    }

    get canAcceptQuote() {
        // Button enabled when either no terms OR all terms accepted
        return this.terms.length === 0 || this.allTermsAccepted;
    }

    handleTermsAcceptanceChanged(e) {
        this.allTermsAccepted = e.detail.allAccepted;
    }

    handleAcceptQuote(e) {
        e.preventDefault();

        if (!this.canAcceptQuote) {
            return;
        }

        // Dispatch event for parent components
        this.dispatchEvent(
            new CustomEvent('quote-accepted', {
                detail: {
                    timestamp: new Date().toISOString(),
                    termsAccepted: this.allTermsAccepted,
                },
                bubbles: true,
                composed: true,
            })
        );
    }

    render() {
        const hasTerms = this.terms && this.terms.length > 0;

        return html`
            <div class="mt-12 mb-8">
                <!-- Terms Accordion -->
                ${hasTerms
                    ? html`
                          <div class="mb-8">
                              <quote-terms-accordion
                                  .terms="${this.terms}"
                                  @terms-acceptance-changed="${this.handleTermsAcceptanceChanged}"
                              ></quote-terms-accordion>
                          </div>
                      `
                    : ''}

                <!-- Accept Quote Button -->
                <div class="flex justify-center">
                    <div class="relative inline-block">
                        <button
                            @click="${this.handleAcceptQuote}"
                            ?disabled="${!this.canAcceptQuote}"
                            class="btn btn-primary btn-lg text-xl font-bold px-12 py-4 min-w-[280px] ${!this
                                .canAcceptQuote
                                ? 'btn-disabled opacity-50 cursor-not-allowed'
                                : ''}"
                            aria-label="${this.canAcceptQuote
                                ? 'Accept quote'
                                : 'Accept all terms to enable this button'}"
                        >
                            ACCEPT QUOTE
                        </button>
                        ${hasTerms && !this.allTermsAccepted
                            ? html`
                                  <span
                                      class="absolute -top-3 -right-3 badge badge-warning badge-sm shadow-lg"
                                  >
                                      Terms Required
                                  </span>
                              `
                            : ''}
                    </div>
                </div>

                <!-- Helper text when terms not accepted -->
                ${hasTerms && !this.allTermsAccepted
                    ? html`
                          <div class="text-center mt-4">
                              <p
                                  class="text-sm text-gray-600 flex items-center justify-center gap-1"
                              >
                                  <info-filled-icon class="inline"></info-filled-icon>
                                  Please accept all terms & conditions above to proceed
                              </p>
                          </div>
                      `
                    : ''}
            </div>
        `;
    }
}

customElements.define('quote-acceptance-section', QuoteAcceptanceSection);
